import type {BusinessAllowedFeature} from '@/types/AfriexBusiness';

/**
 * Merges a sparse 'selected' tree into a full 'template' tree. I used this to
 * hydrate a data from business object to populate the reducer.
 */
export const hydrateAllowedFeatureTree = (
  template: BusinessAllowedFeature[],
  selectedNodes: BusinessAllowedFeature[] | null | undefined,
): BusinessAllowedFeature[] => {
  if (!selectedNodes || selectedNodes.length === 0) {
    return template;
  }

  // Create a Map for O(1) lookup of selected nodes by key
  const selectedMap = new Map(selectedNodes.map((node) => [node.key, node]));

  return template.map((node) => {
    const userNode = selectedMap.get(node.key);

    // Recursively handle children if they exist in the template
    const children = node.children
      ? hydrateAllowedFeatureTree(node.children, userNode?.children)
      : node.children;

    return {
      ...node,
      isSelected: !!userNode?.isSelected,
      children,
    };
  });
};

/**
 * Removes nodes where isSelected is false and no children are selected.
 */
export const pruneAllowedFeatureTree = (
  nodes: BusinessAllowedFeature[],
): BusinessAllowedFeature[] => {
  return (
    nodes
      .map((node) => {
        // Recursively prune children first
        const prunedChildren = node.children
          ? pruneAllowedFeatureTree(node.children)
          : [];

        return {
          ...node,
          children: prunedChildren.length > 0 ? prunedChildren : null,
        };
      })
      // Keep node only if it is selected OR it has pruned children remaining
      .filter(
        (node) =>
          node.isSelected || (node.children && node.children.length > 0),
      )
  );
};
